<script setup lang="ts">
import type { ShowcaseContentListItemDto } from '@repo/api/web/models'

import UiPicture from '~/app/components/Image/UiPicture.vue'

const props = defineProps<{
  value: ShowcaseContentListItemDto
  /** Базовый путь детальной страницы ('/news', '/blog') */
  basePath: string
}>()

const { formatDate } = useFormatter()

const href = computed(() => `${props.basePath}/${props.value.slug}`)
const publishedAt = computed(() =>
  props.value.publishAt
    ? formatDate(props.value.publishAt, Formats.DATE_FORMAT)
    : null,
)
const hasCover = computed(() => Boolean(props.value.coverId))
/** No cover → fixed-ratio media becomes an editorial title plate */
const isTitlePlate = computed(() => !hasCover.value)
</script>

<template>
  <NuxtLink
    :to="href"
    class="content-card content-card--feed group flex flex-col overflow-hidden rounded-md border border-default bg-default"
    :class="isTitlePlate && 'content-card--title-plate'"
  >
    <div
      class="content-card__media relative aspect-video overflow-hidden"
      :class="isTitlePlate
        ? 'bg-linear-to-br from-primary/20 via-primary/10 to-primary/5'
        : 'bg-elevated'"
    >
      <UiPicture
        v-if="hasCover && props.value.coverId"
        :src="props.value.coverId"
        variant="imageFull"
        class="size-full transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100 [&>img]:size-full [&>img]:object-cover"
        sizes="100vw md:50vw lg:400px"
        loading="lazy"
        :alt="props.value.title"
      />

      <!-- Title plate: same aspect frame, headline lives in the media block -->
      <div
        v-else
        class="flex size-full flex-col justify-end gap-2 p-4 sm:p-5"
      >
        <h2 class="line-clamp-4 text-xl leading-tight font-bold tracking-tight text-highlighted transition-colors group-hover:text-primary sm:text-2xl">
          {{ props.value.title }}
        </h2>
      </div>
    </div>

    <div class="content-card__body flex flex-1 flex-col gap-2 p-4">
      <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted">
        <time
          v-if="publishedAt"
          :datetime="props.value.publishAt ?? undefined"
        >
          {{ publishedAt }}
        </time>
      </div>

      <!-- With cover: title stays under the image. Title plate already shows it above. -->
      <h2
        v-if="hasCover"
        class="text-lg font-semibold text-highlighted transition-colors group-hover:text-primary"
      >
        {{ props.value.title }}
      </h2>

      <p
        v-if="props.value.announce"
        class="line-clamp-3 text-sm text-muted"
      >
        {{ props.value.announce }}
      </p>
    </div>
  </NuxtLink>
</template>
